import {
  DownCircleFilled,
  SearchOutlined,
  UpCircleFilled,
} from "@ant-design/icons";
import { Button, Checkbox, DatePicker, Input, Radio, Table } from "antd";
import type { CheckboxGroupProps } from "antd/es/checkbox";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
  useGetCategories,
  useGetCategoriesOnly,
  useGetGroups2,
  useGetSearchExpenses2,
} from "../api/api.get";
import { useMessage } from "../components/notifications/message";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useCurrencyContext } from "../providers/currency.provider";
import { IExpense2, IGroup2 } from "../types/types";
import { capitalize } from "../utils/functions";
import { toFixed, toFixedDisplay } from "../utils/number.utils";
import ExpenseViewModal from "./expenses/components/expense.view.modal";

export default function SearchExpenses() {
  const { currency } = useCurrencyContext();

  const { data: categoriesData } = useGetCategoriesOnly();

  const { data: categorySubcategoryMap } = useGetCategories();

  const { data: groupsData } = useGetGroups2();

  // Search and filter state
  const [searchText, setSearchText] = useState("");

  const [startDate, setStartDate] = useState<dayjs.Dayjs | undefined>(
    dayjs().startOf("M"),
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [categoryFilter, setCategoryFilter] = useState<string>();
  const [subcategoryFilter, setSubcategoryFilter] = useState<string>();

  const { mutateAsync: searchAsync, isPending } = useGetSearchExpenses2();

  const { messageError, notification } = useMessage();

  const [searchResults, setSearchResults] = useState<IExpense2[]>([]);

  const totalAmount =
    searchResults?.reduce((sum, exp) => sum + exp.amount, 0) ?? 0;

  const [viewExpense, setViewExpense] = useState<IExpense2>();

  const handleSearch = () => {
    if (!selectedGroupId) {
      messageError("Please select group");
      return;
    }
    if (!startDate) {
      messageError("Please select start date");
      return;
    }
    if (!endDate) {
      messageError("Please select start date");
      return;
    }
    const daysDiff = endDate.diff(startDate, "D");

    if (daysDiff < 0) {
      messageError("Start date cannot be earlier than end date");
      return;
    }
    const tripOnly = searchMode === "tripOnly";
    const miscOnly = searchMode === "miscOnly";

    searchAsync({
      searchText,
      startDate: startDate!.format("YYYY-MM-DD"),
      endDate: endDate!.format("YYYY-MM-DD"),
      categoryFilter,
      tripOnly,
      miscOnly,
      excludeMisc,
      excludeTrip,
      subcategoryFilter,
      groupId: selectedGroupId,
    })
      .then((results) => setSearchResults(results))
      .catch(() => messageError("Whoops! Something went wrong."));
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "comments",
      key: "comments",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Amount",
      key: "amount",
      render: (item: IExpense2) => (
        <button
          className="font-bold text-[#541eff]"
          onClick={() => setViewExpense(item)}
        >
          {currency}
          {toFixedDisplay(item.amount)}
        </button>
      ),
      sorter: (a: IExpense2, b: IExpense2) => a.amount - b.amount,
      sortDirections: ["descend", "ascend"] as any[],
    },
    {
      title: "Date",
      dataIndex: "expensedate",
      key: "expensedate",
      render: (date: string) => (
        <span>{dayjs(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
    },
    {
      title: "",
      key: "options",
      width: 30,
      render: (_: any, item: IExpense2) => (
        <button
          className="font-bold text-[#541eff]"
          onClick={() => setViewExpense(item)}
        >
          View
        </button>
      ),
    },
  ];

  const filteredExpenses: IExpense2[] = useMemo(() => {
    if (!searchResults) return [];

    if (searchResults) {
      const results: any = searchResults.map((expense) => ({
        key: expense.id,
        name: expense.comments,
        category: expense.category,
        amount: expense.amount,
        expensedate: expense.expenseDate,
        method: expense.cardname,
        cardname: expense.cardname,
        comments: expense.comments,
        created_at: expense.created_at,
        creditcard: expense.creditcard,
        expense_source: expense.expense_source,
        expenseDate: expense.expenseDate,
        groupId: (expense as any).groupId, // TODO: fix this - new backend service sends in camelcase
        id: expense.id,
        subcategory: expense.subcategory,
        tripid: expense.tripid,
        username: expense.username,
      }));

      if (categoryFilter) {
        return results.filter((e: any) => e.category === categoryFilter);
      }
      return results;
    }
  }, [categoryFilter, searchResults]);
  const [isCategorySummaryOpen, setIsCategorySummaryOpen] = useState(false);

  const categoryTotalsMap: Map<string, number> = useMemo(() => {
    if (!filteredExpenses) return new Map<string, number>();

    const map = new Map<string, number>();
    for (const e of filteredExpenses) {
      const existing = map.get(e.category) ?? 0;
      map.set(e.category, toFixed(existing + e.amount));
    }

    return new Map(Array.from(map.entries()).sort(([, a], [, b]) => b - a));
  }, [filteredExpenses]);

  const searchModeOptions: CheckboxGroupProps<string>["options"] = [
    { label: "All", value: "all" },
    { label: "Category/Subcategory", value: "categorySubcategory" },
    { label: "Misc Expenses Only", value: "miscOnly" },
    { label: "Trip Expenses Only", value: "tripOnly" },
  ];

  const [selectedGroupId, setSelectedGroupId] = useState<string>();

  const groupFilters: CheckboxGroupProps<string>["options"] = useMemo(() => {
    if (groupsData) {
      const groups = groupsData.map((g: IGroup2) => {
        return {
          label: capitalize(g.name),
          value: g.id,
        };
      });
      return groups;
    }
    return [];
  }, [groupsData]);

  const [searchMode, setSearchMode] = useState<
    "all" | "miscOnly" | "tripOnly" | "categorySubcategory"
  >("all");

  const [excludeMisc, setExcludeMisc] = useState<boolean>(false);
  const [excludeTrip, setExcludeTrip] = useState<boolean>(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {notification}
      {viewExpense && (
        <ExpenseViewModal
          expense={viewExpense}
          onClose={() => setViewExpense(undefined)}
        />
      )}
      <main className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Search Expenses
            </h1>
            <p className="text-muted-foreground">
              Find and filter your expenses easily
            </p>
          </div>

          {/* Top Panel: Search and Filters */}
          <Card className="bg-white mb-6 border-0 shadow-lg">
            <CardContent className="pt-6">
              <div className="mb-6">
                <p className="text-sm font-semibold mb-2">Group</p>
                {!selectedGroupId && (
                  <p className="text-red-500 text-xs mb-2">
                    Please select group
                  </p>
                )}
                <Radio.Group
                  block
                  className="border py-2 rounded-md"
                  options={groupFilters}
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                />
              </div>
              <div className="space-y-6">
                {/* Date Range Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      Start Date
                    </label>
                    <DatePicker
                      value={startDate}
                      onChange={(date) =>
                        setStartDate(date ? dayjs(date) : undefined)
                      }
                      status={!startDate ? "error" : undefined}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-2">
                      End Date
                    </label>
                    <DatePicker
                      value={endDate}
                      onChange={(date) => setEndDate(dayjs(date))}
                      status={!endDate ? "error" : undefined}
                    />
                  </div>
                </div>

                {/* Search Text Row */}
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-2">
                    Search Text
                  </label>
                  <div className="relative">
                    <Input onChange={(e) => setSearchText(e.target.value)} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Search across expense descriptions, categories or
                    subcategories
                  </p>
                </div>

                <div className=" mb-10 gap-3">
                  <Radio.Group
                    block
                    options={searchModeOptions}
                    defaultValue="all"
                    optionType="button"
                    buttonStyle="solid"
                    onChange={(e) => {
                      if (e.target.value !== "categorySubcategory") {
                        setCategoryFilter(undefined);
                        setSubcategoryFilter(undefined);
                      }
                      setSearchMode(e.target.value);
                    }}
                  />
                </div>
                {/* Filters Row */}

                {searchMode === "categorySubcategory" && (
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-3">
                      Category/Subcategory Filters
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <select
                        className="border rounded-lg px-4 py-2 text-sm"
                        onChange={(e) => setCategoryFilter(e.target.value)}
                      >
                        <option value="">Select Category</option>
                        {categoriesData?.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>

                      <select
                        className="border rounded-lg px-4 py-2 text-sm"
                        onChange={(e) => setSubcategoryFilter(e.target.value)}
                        disabled={
                          !categoryFilter ||
                          !categorySubcategoryMap ||
                          !categorySubcategoryMap[categoryFilter]
                        }
                      >
                        <option value="">Select Subcategory</option>
                        {categoryFilter &&
                        categorySubcategoryMap &&
                        categorySubcategoryMap[categoryFilter] ? (
                          categorySubcategoryMap[categoryFilter].map(
                            (subcat) => (
                              <option key={subcat} value={subcat}>
                                {subcat}
                              </option>
                            ),
                          )
                        ) : (
                          <option value="" disabled>
                            Select a category first
                          </option>
                        )}
                      </select>
                    </div>
                  </div>
                )}

                {/* Exclusion Filters Section */}
                {searchMode === "all" && (
                  <div className="mt-6 pt-6 border-t-2 border-slate-200">
                    <label className="block text-sm font-semibold text-foreground mb-3">
                      Exclusion Filters
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Exclude Misc Expenses */}
                      <div
                        className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:border-amber-400 transition cursor-pointer"
                        onClick={() => {
                          setExcludeMisc(!excludeMisc);
                        }}
                      >
                        <Checkbox
                          checked={excludeMisc}
                          onChange={(e) => {
                            e.preventDefault();
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Exclude Misc Expenses
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Hide other category
                          </p>
                        </div>
                      </div>

                      {/* Exclude Trip Expenses */}
                      <div
                        className="flex items-center gap-3 p-3 border border-rose-200 bg-rose-50 rounded-lg hover:border-rose-400 transition cursor-pointer"
                        onClick={() => setExcludeTrip(!excludeTrip)}
                      >
                        <Checkbox
                          checked={excludeTrip}
                          onChange={(e) => {
                            e.preventDefault();
                          }}
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            Exclude Trip Expenses
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Hide trip-related
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button
                    type="primary"
                    size="middle"
                    disabled={!startDate || !endDate}
                    onClick={() => handleSearch()}
                    loading={isPending}
                  >
                    Search
                  </Button>

                  <Button
                    type="dashed"
                    size="middle"
                    onClick={() => handleSearch()}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Middle Panel: Category Summary (Collapsible) */}
          {searchMode === "all" && (
            <Card className="bg-white mb-6 border-0 shadow-lg">
              <CardHeader
                className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-slate-200 cursor-pointer"
                onClick={() => setIsCategorySummaryOpen(!isCategorySummaryOpen)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isCategorySummaryOpen ? (
                      <DownCircleFilled className="w-5 h-5 text-primary" />
                    ) : (
                      <UpCircleFilled className="w-5 h-5 text-primary" />
                    )}
                    <CardTitle>Expenses by Category</CardTitle>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      Total Categories
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {categoryTotalsMap.size}
                    </p>
                  </div>
                </div>
                <CardDescription>
                  Click to expand and see category breakdown
                </CardDescription>
              </CardHeader>

              {isCategorySummaryOpen && (
                <CardContent className="pt-6">
                  {categoryTotalsMap.size === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground font-medium">
                        No categories to display
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Try adjusting your search filters
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Array.from(categoryTotalsMap.entries()).map(
                        ([cat, value]) => (
                          <div
                            key={cat}
                            className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-primary/50 hover:bg-slate-50 transition"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-purple-400" />
                              <div>
                                <p className="font-semibold text-foreground">
                                  {cat ?? "-"}
                                </p>
                              </div>
                            </div>

                            <p className="text-lg font-bold text-primary">
                              {currency}
                              {value}
                            </p>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Bottom Panel: Results Table */}
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Search Results</CardTitle>
                  <CardDescription>
                    {searchResults?.length ?? 0} expense
                    {searchResults?.length !== 1 ? "s" : ""} found
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <p className="text-2xl font-bold text-primary">
                    ${totalAmount.toFixed(2)}
                  </p>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {searchResults?.length === 0 ? (
                <div className="text-center py-12">
                  <SearchOutlined className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                  <p className="text-muted-foreground font-medium">
                    No expenses found
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try adjusting your search filters
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table
                    dataSource={filteredExpenses}
                    columns={columns}
                    loading={isPending}
                    pagination={{ pageSize: 50 }}
                  />

                  {/* Summary Footer */}
                  <div className="mt-6 pt-4 border-t-2 border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                    <p className="font-semibold text-foreground">
                      Total for Search Results
                    </p>
                    <p className="text-2xl font-bold text-primary">
                      ${totalAmount.toFixed(2)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
